import { useEffect, useMemo, useState } from "react";
import "./GameLauncher.css";

const DEFAULT_DIFFICULTIES = [
  {
    key: "easy",
    label: "Fácil",
    icon: "🟢",
    description: "Menos estímulos y ritmo tranquilo.",
  },
  {
    key: "medium",
    label: "Media",
    icon: "🟡",
    description: "Nivel equilibrado para intervención.",
  },
  {
    key: "hard",
    label: "Difícil",
    icon: "🔴",
    description: "Más estímulos y mayor exigencia.",
  },
];

const CUSTOM_DIFFICULTY = {
  key: "custom",
  label: "Personalizada",
  icon: "⚙️",
  description: "Adapta la actividad a las necesidades del paciente.",
};

function createInitialCustomValues(fields, defaults) {
  const initialValues = {};

  fields.forEach((field) => {
    if (defaults[field.name] !== undefined) {
      initialValues[field.name] = defaults[field.name];
      return;
    }

    if (field.defaultValue !== undefined) {
      initialValues[field.name] = field.defaultValue;
      return;
    }

    if (field.type === "checkbox") {
      initialValues[field.name] = false;
      return;
    }

    if (field.type === "select") {
      initialValues[field.name] =
        field.options?.[0]?.value ?? "";
      return;
    }

    initialValues[field.name] = field.min ?? "";
  });

  return initialValues;
}

function normalizeCustomValues(fields, values) {
  const normalizedValues = {};

  fields.forEach((field) => {
    const currentValue = values[field.name];

    if (field.type === "checkbox") {
      normalizedValues[field.name] = Boolean(currentValue);
      return;
    }

    if (field.type === "number" || !field.type) {
      let numericValue = Number(currentValue);

      if (!Number.isFinite(numericValue)) {
        numericValue = Number(
          field.defaultValue ?? field.min ?? 0
        );
      }

      if (field.min !== undefined) {
        numericValue = Math.max(
          field.min,
          numericValue
        );
      }

      if (field.max !== undefined) {
        numericValue = Math.min(
          field.max,
          numericValue
        );
      }

      normalizedValues[field.name] = numericValue;
      return;
    }

    normalizedValues[field.name] = currentValue;
  });

  return normalizedValues;
}

export default function GameLauncher({
  title,
  description,
  areas = [],
  difficulties = DEFAULT_DIFFICULTIES,
  defaultDifficulty = "easy",
  customFields = [],
  customDefaults = {},
  onStart,
}) {
  const hasCustomSettings = customFields.length > 0;

  const availableDifficulties = useMemo(() => {
    if (!hasCustomSettings) {
      return difficulties;
    }

    const alreadyHasCustom = difficulties.some(
      (difficulty) => difficulty.key === "custom"
    );

    if (alreadyHasCustom) {
      return difficulties;
    }

    return [...difficulties, CUSTOM_DIFFICULTY];
  }, [difficulties, hasCustomSettings]);

  const initialCustomValues = useMemo(
    () =>
      createInitialCustomValues(
        customFields,
        customDefaults
      ),
    [customFields, customDefaults]
  );

  const [selectedDifficulty, setSelectedDifficulty] =
    useState(defaultDifficulty);

  const [customValues, setCustomValues] = useState(
    () => initialCustomValues
  );

  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (
      selectedDifficulty === "custom" &&
      !hasCustomSettings
    ) {
      setSelectedDifficulty(defaultDifficulty);
    }
  }, [
    selectedDifficulty,
    hasCustomSettings,
    defaultDifficulty,
  ]);

  const handleDifficultyChange = (difficultyKey) => {
    setSelectedDifficulty(difficultyKey);
    setFormError("");
  };

  const handleCustomValueChange = (field, value) => {
    setCustomValues((previous) => ({
      ...previous,
      [field.name]: value,
    }));

    setFormError("");
  };

  const resetCustomValues = () => {
    setCustomValues(
      createInitialCustomValues(
        customFields,
        customDefaults
      )
    );

    setFormError("");
  };

  const validateCustomValues = () => {
    for (const field of customFields) {
      const value = customValues[field.name];

      if (
        field.required &&
        (value === "" ||
          value === null ||
          value === undefined)
      ) {
        return `Completa el campo "${field.label}".`;
      }

      if (
        (field.type === "number" || !field.type) &&
        value !== ""
      ) {
        const numericValue = Number(value);

        if (!Number.isFinite(numericValue)) {
          return `"${field.label}" debe ser un número.`;
        }

        if (
          field.min !== undefined &&
          numericValue < field.min
        ) {
          return `"${field.label}" debe ser como mínimo ${field.min}.`;
        }

        if (
          field.max !== undefined &&
          numericValue > field.max
        ) {
          return `"${field.label}" debe ser como máximo ${field.max}.`;
        }
      }

      if (typeof field.validate === "function") {
        const validationResult = field.validate(
          value,
          customValues
        );

        if (validationResult) {
          return validationResult;
        }
      }
    }

    return "";
  };

  const handleStart = () => {
    if (selectedDifficulty !== "custom") {
      onStart(selectedDifficulty);
      return;
    }

    const validationError =
      validateCustomValues();

    if (validationError) {
      setFormError(validationError);
      return;
    }

    const normalizedValues =
      normalizeCustomValues(
        customFields,
        customValues
      );

    onStart({
      difficulty: "custom",
      label: "Personalizada",
      ...normalizedValues,
    });
  };

  const renderCustomField = (field) => {
    const value = customValues[field.name];

    if (field.type === "checkbox") {
      return (
        <label
          key={field.name}
          className="custom-setting-field checkbox-field"
        >
          <div className="custom-checkbox-row">
            <input
              id={`custom-${field.name}`}
              type="checkbox"
              checked={Boolean(value)}
              onChange={(event) =>
                handleCustomValueChange(
                  field,
                  event.target.checked
                )
              }
            />

            <span>{field.label}</span>
          </div>

          {field.help && (
            <small>{field.help}</small>
          )}
        </label>
      );
    }

    if (field.type === "select") {
      return (
        <label
          key={field.name}
          className="custom-setting-field"
        >
          <span>{field.label}</span>

          <select
            value={value ?? ""}
            onChange={(event) =>
              handleCustomValueChange(
                field,
                event.target.value
              )
            }
          >
            {field.options?.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>

          {field.help && (
            <small>{field.help}</small>
          )}
        </label>
      );
    }

    return (
      <label
        key={field.name}
        className="custom-setting-field"
      >
        <span>{field.label}</span>

        <div className="custom-number-wrapper">
          <input
            type="number"
            min={field.min}
            max={field.max}
            step={field.step ?? 1}
            value={value ?? ""}
            required={field.required}
            onChange={(event) =>
              handleCustomValueChange(
                field,
                event.target.value
              )
            }
          />

          {field.unit && (
            <span className="custom-field-unit">
              {field.unit}
            </span>
          )}
        </div>

        {field.help && (
          <small>{field.help}</small>
        )}
      </label>
    );
  };

  return (
    <div className="game-launcher">
      <div className="game-launcher-card">
        <header className="game-launcher-header">
          <span className="game-launcher-badge">
            Juego interactivo
          </span>

          <h1>{title}</h1>

          {description && <p>{description}</p>}
        </header>

        {areas.length > 0 && (
          <div className="game-launcher-areas">
            {areas.map((area) => (
              <span key={area}>{area}</span>
            ))}
          </div>
        )}

        <section className="difficulty-section">
          <h2>Selecciona dificultad</h2>

          <div className="difficulty-grid">
            {availableDifficulties.map(
              (difficulty) => (
                <button
                  key={difficulty.key}
                  type="button"
                  className={
                    selectedDifficulty ===
                    difficulty.key
                      ? "difficulty-card active"
                      : "difficulty-card"
                  }
                  onClick={() =>
                    handleDifficultyChange(
                      difficulty.key
                    )
                  }
                >
                  <span className="difficulty-icon">
                    {difficulty.icon}
                  </span>

                  <strong>
                    {difficulty.label}
                  </strong>

                  <small>
                    {difficulty.description}
                  </small>
                </button>
              )
            )}
          </div>
        </section>

        {selectedDifficulty === "custom" &&
          hasCustomSettings && (
            <section className="custom-settings-section">
              <div className="custom-settings-heading">
                <div>
                  <h2>
                    Configuración personalizada
                  </h2>

                  <p>
                    Ajusta la actividad según las
                    necesidades del paciente.
                  </p>
                </div>

                <button
                  type="button"
                  className="reset-custom-settings"
                  onClick={resetCustomValues}
                >
                  Restablecer
                </button>
              </div>

              <div className="custom-settings-grid">
                {customFields.map(
                  renderCustomField
                )}
              </div>

              {formError && (
                <div
                  className="custom-settings-error"
                  role="alert"
                >
                  {formError}
                </div>
              )}
            </section>
          )}

        <button
          type="button"
          className="game-start-button"
          onClick={handleStart}
        >
          <i className="fa-solid fa-play" />
          Empezar
        </button>
      </div>
    </div>
  );
}